import { createStore, del, get, set, update } from 'idb-keyval';
import { authorizedFetch } from '$lib/api/client';
import { exclusive } from '$lib/state/locks';

/**
 * Shopping-list ticks made without a connection (typical in a supermarket basement) are queued here
 * and replayed later. Only idempotent PUT/DELETE requests are queued, so replaying twice is harmless.
 */
export type OutboxRequest = { method: 'PUT' | 'DELETE'; path: string };

/** A queued request. The id tells this version apart from a newer request for the same path. */
export type OutboxEntry = OutboxRequest & { id: string };

/** How one send went: sent, worth retrying later, signed out, or refused for good. */
export type SendOutcome =
	| { kind: 'sent' }
	| { kind: 'retry'; retryAt?: number }
	| { kind: 'unauthorized' }
	| { kind: 'rejected' };

export type FlushResult = {
	sent: number;
	/** Requests the server refused for good; they are no longer queued. */
	rejected: OutboxEntry[];
	stopped: 'done' | 'retry' | 'unauthorized' | 'cancelled';
	/** When the server asked us to wait (Retry-After), in epoch milliseconds. */
	retryAt?: number;
};

const store = createStore('madplan-offline', 'outbox');
const KEY = 'entries';
const RETRY_AT_KEY = 'retryAt';
const LOCK = 'madplan-outbox';
const TRANSIENT_STATUSES = new Set([408, 429]);

/** Bumped by {@link clearOutbox}, so a flush running at sign-out stops. */
let generation = 0;
let flushes: Promise<unknown> = Promise.resolve();

export async function pendingEntries(): Promise<OutboxEntry[]> {
	return (await get<OutboxEntry[]>(KEY, store)) ?? [];
}

/** Queues a request; a newer request for the same path replaces the older one. */
export async function enqueue(request: OutboxRequest): Promise<OutboxEntry> {
	const entry = { ...request, id: crypto.randomUUID() };
	await update<OutboxEntry[]>(
		KEY,
		(entries = []) => [...entries.filter((e) => e.path !== entry.path), entry],
		store
	);
	return entry;
}

/** Removes exactly this version; a newer request for the same path stays queued. */
async function removeEntry(id: string): Promise<void> {
	await update<OutboxEntry[] | undefined>(
		KEY,
		(entries) => entries?.filter((e) => e.id !== id),
		store
	);
}

async function sendEntry(entry: OutboxEntry): Promise<Response> {
	return authorizedFetch(new Request(entry.path, { method: entry.method }));
}

/** Network failures, timeouts, rate limits and server errors are retried; other refusals are final. */
export function classify(response: Response, now = Date.now()): SendOutcome {
	if (response.ok) return { kind: 'sent' };
	// authorizedFetch already refreshed the token and retried once.
	if (response.status === 401) return { kind: 'unauthorized' };
	if (TRANSIENT_STATUSES.has(response.status) || response.status >= 500) {
		return { kind: 'retry', retryAt: retryAfter(response.headers.get('Retry-After'), now) };
	}
	return { kind: 'rejected' };
}

/** Retry-After is either a number of seconds or an HTTP date. */
function retryAfter(header: string | null, now: number): number | undefined {
	if (!header?.trim()) return undefined;
	const seconds = Number(header);
	if (Number.isFinite(seconds)) return now + seconds * 1000;
	const date = Date.parse(header);
	return Number.isNaN(date) ? undefined : date;
}

async function attemptSend(
	send: (entry: OutboxEntry) => Promise<Response>,
	entry: OutboxEntry
): Promise<SendOutcome> {
	try {
		return classify(await send(entry));
	} catch {
		return { kind: 'retry' };
	}
}

/**
 * Sends queued requests oldest first, one flush at a time across browser tabs, and stops at the
 * first request that cannot be sent now. Requests queued during the flush are sent in the same run.
 */
export function flushOutbox(
	send: (entry: OutboxEntry) => Promise<Response> = sendEntry
): Promise<FlushResult> {
	const flush = flushes.then(() => exclusive(LOCK, () => drain(send, generation)));
	flushes = flush.catch(() => {});
	return flush;
}

async function drain(
	send: (entry: OutboxEntry) => Promise<Response>,
	startedIn: number
): Promise<FlushResult> {
	const result: FlushResult = { sent: 0, rejected: [], stopped: 'done' };
	const retryAt = await get<number>(RETRY_AT_KEY, store);
	if (retryAt && retryAt > Date.now()) return { ...result, stopped: 'retry', retryAt };

	for (let entry = await oldest(); entry; entry = await oldest()) {
		const outcome = await attemptSend(send, entry);
		if (startedIn !== generation) return { ...result, stopped: 'cancelled' };

		if (outcome.kind === 'unauthorized') return { ...result, stopped: 'unauthorized' };
		if (outcome.kind === 'retry') {
			if (outcome.retryAt) await set(RETRY_AT_KEY, outcome.retryAt, store);
			return { ...result, stopped: 'retry', retryAt: outcome.retryAt };
		}

		await removeEntry(entry.id);
		if (outcome.kind === 'sent') result.sent++;
		else result.rejected.push(entry);
	}

	if (retryAt) await del(RETRY_AT_KEY, store);
	return result;
}

async function oldest(): Promise<OutboxEntry | undefined> {
	return (await pendingEntries())[0];
}

/** Empties the queue at sign-out; a flush that is running stops before its next request. */
export async function clearOutbox(): Promise<void> {
	generation++;
	await Promise.all([del(KEY, store), del(RETRY_AT_KEY, store)]);
}
